import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { ContentLayout, FormField, Input } from '@amzn/awsui-components-react';

import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import Footer from '../components/Footer';
import GandalfFlashbar from '../components/GandalfFlashbar';
import ChooseSignInMethod from '../components/ChooseSignInMethod';
import usePageTitle from '../hooks/usePageTitle';
import { useSignInPageSetup } from '../hooks/useSignInPageSetup';
import {
  AuthFactorHintValues,
  IDP_URL_PARAM,
  getSocialAuthFactorHint,
} from '../constants/auth';
import {
  IDP_ESSO,
  IdPs,
  isEltiProvider,
  isStandardProvider,
} from '../constants/providers';
import useLoginMethod from '../hooks/useLoginMethod';
import useProviders from '../hooks/useProviders';
import metricsService from '../services/metrics';
import {
  GANDALF_SESSION,
  IMPRESSIONS,
  LOGIN_ACTION_TIMESTAMP_KEY,
  LOGIN_EMAIL_HASH_KEY,
  LOGIN_ENTRY_METHOD_KEY,
  LOGIN_START_TIMESTAMP_KEY,
  SimplifiedLoginRumEvent,
} from '../constants/metrics';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import messages from '../i18n/simplifiedSignIn.messages';
import globalMessages from '../i18n/global.messages';
import styles from './SimplifiedSignIn.module.css';
import ScreenReaderHidden from '../components/ScreenReaderHidden';
import googleLogo from '../assets/google-logo.svg';
import appleLogo from '../assets/apple-logo.svg';
import githubLogo from '../assets/github-logo.svg';
import amazonLogo from '../assets/lwa.svg';
import keyIcon from '../assets/key.svg';
import infoIcon from '../assets/status-info.svg';
import { validateEmail } from '../utils/email';
import { hashEmail } from '../utils/hashEmail';
import Cookies from 'js-cookie';
import { VERIFY_EMAIL_COOKIE_NAME } from '../components/postAuth/VerifyEmail';
import providersService from '../services/providers';
import { rumService } from '../services/rum';
import { AppURL } from '../constants/urls';
import { LAST_USED_PROVIDER_KEY } from '../constants/storage';
import { getSessionState } from '../hooks/useSignIn';
import { queryApi } from '../hooks/useGetPublicIdpDetails';
import { resolveIdProvider } from '../components/resolveIdProvider';
import logger from '../utils/logger';
import { T_AND_C_TERMS } from '../constants/links';
import eulaMessages from '../i18n/eula.messages';

function SimplifiedSignIn({ config }: { config: AppConfig }) {
  usePageTitle();
  const { formatMessage } = useIntl();
  const { enableCookieComponent, handleRedirectToIdp } =
    useSignInPageSetup(config);
  const metricsPublisher = useRef(
    metricsService.getPublisher('SimplifiedSignInPage')
  );
  // OAuth client_id from the request URL, tagged onto the funnel RUM events so
  // the dashboards can break the funnel down by app client. Matches how
  // SimplifiedLoginConversionSuccess derives clientId in AuthResponse.tsx.
  const clientId =
    new URLSearchParams(window.location.search).get('client_id') || 'NA';
  const {
    isLoading,
    error: loginMethodError,
    loginMethods,
    fetchLoginMethod,
    resetLoginMethods,
  } = useLoginMethod(config);

  // Computed synchronously (not in an effect) so the first paint renders the
  // redirect shell, not the form — otherwise the UI flashes before the effect
  // below federates away. Condition must match that effect's redirect guard.
  const [redirectingToPreferredIdp] = useState(() => {
    const preferredIdp = new URLSearchParams(window.location.search).get(
      IDP_URL_PARAM
    );
    return Boolean(preferredIdp && preferredIdp.toLowerCase() !== 'cognito');
  });

  // Handle identity_provider URL param (e.g. from replayAuth retry)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const preferredIdp = urlParams.get(IDP_URL_PARAM);
    if (preferredIdp && preferredIdp.toLowerCase() !== 'cognito') {
      // Social IdP values (e.g. `GithubSocial`) direct the user to AWS Builder
      // ID with a social login hint instead of federating into a Cognito IdP.
      const socialAuthFactorHint = getSocialAuthFactorHint(preferredIdp);
      if (socialAuthFactorHint) {
        const builderIdProvider = providersService.transformNameToIdProvider(
          IdPs.AWSBuilderID,
          config.gandalfDomain
        );
        metricsPublisher.current.publishCounterMonitor(
          'PreferredIdpSocialRedirect',
          1
        );
        handleRedirectToIdp(builderIdProvider, undefined, socialAuthFactorHint);
        return;
      }
      metricsPublisher.current.publishCounterMonitor('PreferredIdpRedirect', 1);
      if (preferredIdp === IdPs.OTP) {
        sessionStorage.setItem('otp_prefill_email', '');
      }
      // Non-standard (ESSO/enterprise) IDPs may live in a non-main Cognito user
      // pool, so resolve full IDP details (userPoolId/clientId/idpType) before
      // redirecting; otherwise federation targets the main pool and fails.
      // Standard providers resolve synchronously to a main-pool provider.
      resolveIdProvider(config, preferredIdp)
        .then((provider) => handleRedirectToIdp(provider))
        .catch(() =>
          handleRedirectToIdp(
            providersService.transformNameToIdProvider(
              preferredIdp,
              config.gandalfDomain
            )
          )
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [helpExpanded, setHelpExpanded] = useState(false);
  const [showChooseMethod, setShowChooseMethod] = useState(false);
  // Stays true from the moment the user submits until the page navigates away
  // to the IdP. Prevents repeat submissions during the API-lookup + redirect
  // window: `isLoading` alone re-enables the button as soon as the loginMethod
  // API resolves, but the actual OAuth redirect fires slightly later, and each
  // extra submit starts a new Amplify federatedSignIn that overwrites the
  // single `oauth_state` slot — causing "Invalid state in OAuth flow" when an
  // earlier attempt's callback returns. Reset only on error / when the chooser
  // is shown, so the user can still retry those paths.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstHelpTileRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (helpExpanded) {
      firstHelpTileRef.current?.focus();
    }
  }, [helpExpanded]);
  const [checkingSession, setCheckingSession] = useState(() => {
    if (config.enableAuthSessionReuse !== 'true') return false;
    try {
      const raw = localStorage.getItem(LAST_USED_PROVIDER_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!parsed?.idp || !parsed?.createdAt) return false;
      if (providersService.isCreatedAtTooOld(parsed.createdAt)) return false;
      return true;
    } catch {
      return false;
    }
  });

  // Session reuse: if there's an active session, auto-redirect (SSO)
  useEffect(() => {
    if (!checkingSession) return;

    const raw = localStorage.getItem(LAST_USED_PROVIDER_KEY);
    if (!raw) {
      setCheckingSession(false);
      return;
    }
    let lastUsed;
    try {
      lastUsed = JSON.parse(raw);
    } catch {
      setCheckingSession(false);
      return;
    }
    if (!lastUsed?.idp) {
      setCheckingSession(false);
      return;
    }
    const storedIdp = lastUsed.idp;

    // Fetch full IDP details (including idpType) before session check
    queryApi(config, storedIdp.idp)
      .catch((error: Error) => {
        metricsPublisher.current.publishCounterMonitor(
          'SSO:SessionStateError:QueryApi',
          1
        );
        throw error;
      })
      .then((idpDetails) => {
        if (!idpDetails?.authEndpoint) {
          setCheckingSession(false);
          return;
        }

        const idp = {
          ...storedIdp,
          url: providersService.getIdpUrl(
            storedIdp.idp,
            idpDetails.authEndpoint
          ),
          userPoolId: idpDetails.userPoolId,
          clientId: idpDetails.clientId,
          idpType: idpDetails.idpType,
        };

        // ELTI providers are not eligible for session reuse
        if (isEltiProvider(idp.idpType)) {
          setCheckingSession(false);
          return;
        }

        // APN provider is not eligible for session reuse now that APN is deprecated
        if (idp.idp === IdPs.AWSPartnerNetwork) {
          setCheckingSession(false);
          return;
        }

        return getSessionState(config.sessionApiPath)
          .catch((error: Error) => {
            metricsPublisher.current.publishCounterMonitor(
              'SSO:SessionStateError:GetSessionState',
              1
            );
            throw error;
          })
          .then((hasActiveSession: boolean) => {
            if (hasActiveSession) {
              logger.debug(
                'SimplifiedSignIn: Active session found, redirecting.'
              );
              metricsPublisher.current.publishCounterMonitor(
                'SSO:SessionStateActive',
                1
              );

              if (
                config.enableSkipIDPAuthInGandalfSession === 'true' &&
                config.enableGandalfSession === 'true' &&
                !isStandardProvider(idp.idp) &&
                idp.idpType === IDP_ESSO
              ) {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set(IDP_URL_PARAM, GANDALF_SESSION);
                handleRedirectToIdp({
                  idp: GANDALF_SESSION,
                  userPoolId: config.authUserPoolId,
                  url: `https://${
                    config.gandalfDomain
                  }/oauth2/authorize?${urlParams.toString()}`,
                  clientId: config.authClientId,
                });
              } else {
                handleRedirectToIdp(idp);
              }
            } else {
              metricsPublisher.current.publishCounterMonitor(
                'SSO:SessionStateInactive',
                1
              );
              setCheckingSession(false);
            }
          });
      })
      .catch(() => {
        metricsPublisher.current.publishCounterMonitor(
          'SSO:SessionStateError',
          1
        );
        setCheckingSession(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { providers: appClientProviders } = useProviders(config);
  const builderIdEnabled =
    !appClientProviders ||
    appClientProviders.some((p) => p.idp === IdPs.AWSBuilderID);

  const enableGoogle = config.enableGoogleSignIn !== 'false';
  const enableApple = config.enableAppleSignIn !== 'false';
  const enableGithub = config.enableGithubSignIn !== 'false';
  const enableAmazon = config.enableAmazonSignIn !== 'false';
  const hasSocialProviders =
    builderIdEnabled &&
    (enableGoogle || enableApple || enableGithub || enableAmazon);

  const [essoDisplayNames, setEssoDisplayNames] = useState<
    Record<string, string>
  >({});

  // Cache of fully-resolved providers (including non-main pool details) keyed
  // by IDP name. Populated when display names are fetched for the multi-IDP
  // chooser so the subsequent redirect can reuse the result instead of making
  // a second GetPublicIdpDetails call.
  const resolvedProviderCache = useRef<Record<string, IdProvider>>({});

  // Resolve an IDP to a full provider, reusing the cache when available and
  // populating it on a miss. Used by both the single-IDP SSO redirect and the
  // multi-IDP chooser so a given IDP is only looked up once.
  const resolveProviderCached = useCallback(
    async (idpName: string): Promise<IdProvider> => {
      const cached = resolvedProviderCache.current[idpName];
      if (cached) return cached;
      const provider = await resolveIdProvider(config, idpName);
      resolvedProviderCache.current[idpName] = provider;
      return provider;
    },
    [config]
  );

  // Fetch display names for non-standard (ESSO) providers when multiple login
  // methods are returned. Uses resolveIdProvider so the full provider (with any
  // non-main pool details) is cached for reuse by the redirect.
  useEffect(() => {
    if (loginMethods && loginMethods.length > 1) {
      const essoMethods = loginMethods.filter((m) => !isStandardProvider(m));
      if (essoMethods.length > 0) {
        Promise.all(
          essoMethods.map((idp) =>
            resolveIdProvider(config, idp)
              .then((provider) => {
                resolvedProviderCache.current[idp] = provider;
                return { idp, displayName: provider.lastUsedIdpDisplayName };
              })
              .catch(() => ({ idp, displayName: undefined }))
          )
        ).then((results) => {
          const names: Record<string, string> = {};
          for (const { idp, displayName } of results) {
            if (displayName) names[idp] = displayName;
          }
          setEssoDisplayNames(names);
        });
      }
    }
  }, [loginMethods, config]);

  // When loginMethod API returns exactly 1 IDP, redirect to it (SSO flow)
  // When more than 1 IDP, show Choose Sign In Method inline
  useEffect(() => {
    if (loginMethods?.length === 1) {
      const idpName = loginMethods[0];
      metricsPublisher.current.publishCounterMonitor('SSORedirect', 1);
      rumService.recordEvent('SimplifiedSignInSSORedirect', {
        idp: idpName,
        provider: idpName,
        clientId,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
      // Flush before the redirect navigates the page away. The redirect fires
      // inside the resolveProviderCached().then() below (which can resolve
      // synchronously for standard providers), so dispatch here to guarantee
      // this event — the by-IdP conversion denominator — is not dropped.
      rumService.dispatchBeacon();
      if (idpName === IdPs.OTP) {
        sessionStorage.setItem('otp_prefill_email', email);
      }
      // Non-standard (ESSO/enterprise) IDPs may live in a non-main Cognito
      // user pool, so resolve full IDP details (userPoolId/clientId/idpType)
      // before redirecting. Standard providers resolve synchronously to a
      // main-pool provider inside resolveIdProvider.
      resolveProviderCached(idpName)
        .then((provider) => {
          handleRedirectToIdp(provider, email);
        })
        .catch(() => {
          const provider = providersService.transformNameToIdProvider(
            idpName,
            config.gandalfDomain
          );
          handleRedirectToIdp(provider, email);
        });
    } else if (loginMethods && loginMethods.length > 1) {
      setShowChooseMethod(true);
      // Multi-IdP: no auto-redirect — the user picks on the chooser, so allow
      // interaction again.
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMethods, config.gandalfDomain, handleRedirectToIdp]);

  // Capture metric when loginMethod API returns an error
  useEffect(() => {
    if (loginMethodError) {
      // Re-enable the form so the user can retry after a failed lookup.
      setIsSubmitting(false);
      metricsPublisher.current.publishCounterMonitor('Error', 1);
      metricsPublisher.current.publishStringTruncate(
        'ErrorMsg',
        loginMethodError.toString()
      );
      metricsPublisher.current.publishStringTruncate(
        'UserAgent',
        navigator.userAgent
      );
      rumService.recordEvent('SimplifiedSignInError', {
        error: loginMethodError.message,
        clientId,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
    }
  }, [loginMethodError, clientId]);

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    sessionStorage.setItem(LOGIN_START_TIMESTAMP_KEY, Date.now().toString());
    // Use recordImpression (not recordEvent) so the impression is buffered and
    // replayed if the RUM client is not yet initialized on mount. Otherwise the
    // impression is dropped on early page loads, under-counting the page
    // conversion denominator. See rumService.recordImpression.
    rumService.recordImpression(SimplifiedLoginRumEvent.PAGE_IMPRESSION, {
      loginHintEnabled: config.enableBIDLoginHintPrefill === 'true',
      clientId,
      url: window.location.href,
      path: `${window.location.pathname}${window.location.search}`,
    });
    rumService.dispatchBeacon();
    Cookies.remove(VERIFY_EMAIL_COOKIE_NAME);
    const handlePageShow = (event: PageTransitionEvent) => {
      const isBFEventOld =
        window.performance &&
        window.performance.navigation.type ===
          window.performance.navigation.TYPE_BACK_FORWARD;
      const navEntry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      const isBFEventNew = navEntry?.type === 'back_forward';
      if (isBFEventOld || isBFEventNew || event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [config.enableBIDLoginHintPrefill, clientId]);

  const handleContinue = (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateEmail(email);
    setIsValidEmail(isValid);
    if (!isValid) return;
    // Guard against repeat submits during the lookup + redirect window.
    if (isSubmitting) return;
    setIsSubmitting(true);
    const actionTs = Date.now();
    const loginStartTs = sessionStorage.getItem(LOGIN_START_TIMESTAMP_KEY);
    const timeToActionMs = loginStartTs
      ? actionTs - parseInt(loginStartTs, 10)
      : undefined;
    // Persist a stable per-email RUM token (SHA-256) so it can be attached
    // inline to this session's events here and post-auth in AuthResponse.tsx.
    // Inline (not addSessionAttributes) because the RUM client may not exist
    // yet pre-consent; see utils/hashEmail for the privacy note.
    hashEmail(email)
      .catch(() => undefined) // Hashing must never block login.
      .then((emailHash) => {
        // Keep storage in sync with THIS email (clears any stale value so a
        // prior submit's token can't be mis-attributed here or in AuthResponse).
        if (emailHash) {
          sessionStorage.setItem(LOGIN_EMAIL_HASH_KEY, emailHash);
        } else {
          sessionStorage.removeItem(LOGIN_EMAIL_HASH_KEY);
        }
        rumService.recordEvent(SimplifiedLoginRumEvent.EMAIL_SUBMITTED, {
          loginHintEnabled: config.enableBIDLoginHintPrefill === 'true',
          clientId,
          emailHash,
          url: window.location.href,
        });
        if (timeToActionMs !== undefined) {
          rumService.recordEvent(SimplifiedLoginRumEvent.LOGIN_TIME_TO_ACTION, {
            durationMs: timeToActionMs,
            durationSec: Math.round(timeToActionMs / 1000),
            actionType: 'email',
            clientId,
            emailHash,
          });
        }
        sessionStorage.setItem(LOGIN_ACTION_TIMESTAMP_KEY, actionTs.toString());
        sessionStorage.setItem(LOGIN_ENTRY_METHOD_KEY, 'email');
        fetchLoginMethod(email);
      });
  };

  const handleSocialSignIn = (providerName: string) => {
    // Guard against repeat clicks during the redirect window (each extra
    // federatedSignIn overwrites Amplify's single oauth_state slot).
    if (isSubmitting) return;
    setIsSubmitting(true);
    const actionTs = Date.now();
    const loginStartTs = sessionStorage.getItem(LOGIN_START_TIMESTAMP_KEY);
    const timeToActionMs = loginStartTs
      ? actionTs - parseInt(loginStartTs, 10)
      : undefined;
    rumService.recordEvent(SimplifiedLoginRumEvent.SOCIAL_SIGN_IN_CLICKED, {
      // Social logins always federate through AWS Builder ID (the social
      // provider is only an auth-factor hint). Tag idp=AWSBuilderID so the
      // by-IdP conversion widget groups this click in the same row as its
      // SimplifiedLoginConversionSuccess (which also carries idp=AWSBuilderID),
      // while `provider` keeps the granular social name for social-breakdown
      // widgets.
      idp: IdPs.AWSBuilderID,
      provider: providerName,
      clientId,
      url: window.location.href,
    });
    if (timeToActionMs !== undefined) {
      rumService.recordEvent(SimplifiedLoginRumEvent.LOGIN_TIME_TO_ACTION, {
        durationMs: timeToActionMs,
        durationSec: Math.round(timeToActionMs / 1000),
        actionType: 'social',
        clientId,
      });
    }
    sessionStorage.setItem(LOGIN_ACTION_TIMESTAMP_KEY, actionTs.toString());
    sessionStorage.setItem(LOGIN_ENTRY_METHOD_KEY, 'social');
    const provider = providersService.transformNameToIdProvider(
      IdPs.AWSBuilderID,
      config.gandalfDomain
    );
    // Flush the click / time-to-action events before the redirect navigates
    // the page away, mirroring the SwitchToOldUI handler. Without this the
    // batched RUM events risk being dropped by the navigation.
    rumService.dispatchBeacon();
    handleRedirectToIdp(provider, undefined, providerName);
  };

  if (redirectingToPreferredIdp || checkingSession) {
    return (
      <div className={styles.page}>
        <div className={styles.stickyHeader}>
          <TopHeader config={config} />
        </div>
        <div className={styles.mainContent}>
          <Main config={config}>
            <ContentLayout defaultPadding maxContentWidth={1040} />
          </Main>
        </div>
        <Footer enableCookieComponent={enableCookieComponent} />
      </div>
    );
  }

  if (showChooseMethod && loginMethods) {
    return (
      <div className={styles.page}>
        <div className={styles.stickyHeader}>
          <TopHeader config={config} />
        </div>
        <div className={styles.mainContent}>
          <Main config={config}>
            <ContentLayout defaultPadding maxContentWidth={1040}>
              <GandalfFlashbar />
              <ChooseSignInMethod
                config={config}
                email={email}
                loginMethods={loginMethods}
                displayNames={essoDisplayNames}
                resolveProvider={resolveProviderCached}
                onChangeEmail={() => {
                  setShowChooseMethod(false);
                  resetLoginMethods();
                }}
                onRedirectToIdp={handleRedirectToIdp}
              />
            </ContentLayout>
          </Main>
        </div>
        <Footer enableCookieComponent={enableCookieComponent} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.stickyHeader}>
        <TopHeader config={config} />
      </div>
      <div className={styles.mainContent}>
        <Main config={config}>
          <ContentLayout defaultPadding maxContentWidth={1040}>
            <GandalfFlashbar />
            <div className={styles.container}>
              <div className={styles.card}>
                <h1 className={styles.header}>
                  {formatMessage(messages.header)}
                </h1>

                {loginMethodError && (
                  <div className={styles.errorBanner} role="alert">
                    <svg
                      className={styles.errorBannerIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="8" cy="8" r="7.5" stroke="#d91515" />
                      <path
                        d="M8 4v5M8 11v1"
                        stroke="#d91515"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{formatMessage(messages.loginMethodError)}</span>
                  </div>
                )}

                <form onSubmit={handleContinue} noValidate>
                  <FormField
                    errorText={
                      !isValidEmail ? formatMessage(messages.emailInvalid) : ''
                    }
                    i18nStrings={{ errorIconAriaLabel: '' }}
                  >
                    <span className={styles.inputLabel}>
                      {formatMessage(messages.emailLabel)}
                    </span>
                    <Input
                      type="email"
                      value={email}
                      ariaLabel={formatMessage(messages.emailLabel)}
                      placeholder={formatMessage(messages.emailPlaceholder)}
                      onChange={({ detail }) => {
                        setEmail(detail.value);
                        setIsValidEmail(true);
                      }}
                      data-testid="simplified-email-input"
                    />
                  </FormField>

                  <button
                    type="submit"
                    className={styles.primaryButton}
                    data-testid="simplified-continue-button"
                    disabled={isLoading || isSubmitting}
                  >
                    {formatMessage(messages.continueButton)}
                  </button>
                </form>

                {/* Separator */}
                {hasSocialProviders && (
                  <div className={styles.separator}>
                    <div className={styles.separatorLine} />
                    <span className={styles.separatorText}>
                      {formatMessage(messages.orContinueWith)}
                    </span>
                    <div className={styles.separatorLine} />
                  </div>
                )}

                {/* Social sign-in tiles */}
                {hasSocialProviders && (
                  <div className={styles.socialTiles}>
                    {enableGoogle && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.GOOGLE)
                        }
                        data-testid="simplified-google-button"
                      >
                        <img
                          src={googleLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.googleLabel)}
                        </span>
                      </button>
                    )}
                    {enableApple && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.APPLE)
                        }
                        data-testid="simplified-apple-button"
                      >
                        <img
                          src={appleLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.appleLabel)}
                        </span>
                      </button>
                    )}
                    {enableGithub && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.GITHUB)
                        }
                        data-testid="simplified-github-button"
                      >
                        <img
                          src={githubLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.githubLabel)}
                        </span>
                      </button>
                    )}
                    {enableAmazon && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.AMAZON)
                        }
                        data-testid="simplified-amazon-button"
                      >
                        <img
                          src={amazonLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.amazonLabel)}
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {/* Legal text */}
                <p className={styles.legalText}>
                  {formatMessage(messages.legalText, {
                    customerAgreement: (
                      <a
                        href="https://aws.amazon.com/agreement/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.customerAgreement)}
                        <ScreenReaderHidden>
                          {formatMessage(globalMessages.opensInNewTab)}
                        </ScreenReaderHidden>
                      </a>
                    ),
                    serviceTerms: (
                      <a
                        href="https://aws.amazon.com/service-terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.serviceTerms)}
                        <ScreenReaderHidden>
                          {formatMessage(globalMessages.opensInNewTab)}
                        </ScreenReaderHidden>
                      </a>
                    ),
                    privacyNotice: (
                      <a
                        href="https://aws.amazon.com/privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.privacyNotice)}
                        <ScreenReaderHidden>
                          {formatMessage(globalMessages.opensInNewTab)}
                        </ScreenReaderHidden>
                      </a>
                    ),
                    learnerTerms: (
                      <a
                        href={T_AND_C_TERMS}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={formatMessage(
                          eulaMessages.termsAndConditionsAndOrganizationAriaLabel
                        )}
                      >
                        {formatMessage(messages.learnerTerms)}
                      </a>
                    ),
                  })}
                </p>
              </div>

              <div className={styles.helpRegion}>
                {!helpExpanded && (
                  <div className={styles.helpLink}>
                    <button
                      type="button"
                      onClick={() => setHelpExpanded(true)}
                      data-testid="help-toggle"
                      aria-expanded="false"
                    >
                      {formatMessage(messages.getHelpSigningIn)}
                      <span className={styles.chevron} aria-hidden="true">
                        ▼
                      </span>
                    </button>
                  </div>
                )}
                {helpExpanded && (
                  <div className={styles.helpSection}>
                    <div className={styles.helpDivider} />
                    <p className={styles.helpToggleLabel}>
                      {formatMessage(messages.getHelpSigningIn)}:
                    </p>
                    <div className={styles.helpTiles}>
                      <button
                        type="button"
                        className={styles.helpTile}
                        ref={firstHelpTileRef}
                        onClick={() => {
                          metricsPublisher.current.publishCounterMonitor(
                            'SwitchToOldUI',
                            1
                          );
                          rumService.recordEvent(
                            'SimplifiedSignInSwitchToOldUI',
                            {
                              clientId,
                              url: window.location.href,
                              userAgent: navigator.userAgent,
                              path: `${window.location.pathname}${window.location.search}`,
                            }
                          );
                          rumService.dispatchBeacon();
                          sessionStorage.removeItem(LOGIN_START_TIMESTAMP_KEY);
                          sessionStorage.removeItem(LOGIN_ACTION_TIMESTAMP_KEY);
                          const params = new URLSearchParams(
                            window.location.search
                          );
                          params.set('fromSimplified', 'true');
                          window.location.href = `${
                            AppURL.LoginClassic
                          }?${params.toString()}`;
                        }}
                        data-testid="switch-old-ui"
                      >
                        <img
                          src={keyIcon}
                          alt=""
                          className={styles.helpTileIcon}
                        />
                        <span className={styles.helpTileLabel}>
                          {formatMessage(messages.switchToOldUi)}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.helpTile}
                        onClick={() => {
                          rumService.recordEvent('SimplifiedSignInFAQClicked', {
                            clientId,
                            url: window.location.href,
                          });
                          rumService.dispatchBeacon();
                          const params = new URLSearchParams(
                            window.location.search
                          );
                          const query = params.toString();
                          window.location.href = query
                            ? `${AppURL.Help}?${query}`
                            : AppURL.Help;
                        }}
                        data-testid="faq-link"
                      >
                        <img
                          src={infoIcon}
                          alt=""
                          className={styles.helpTileIcon}
                        />
                        <span className={styles.helpTileLabel}>
                          {formatMessage(messages.faq)}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ContentLayout>
        </Main>
      </div>
      <Footer enableCookieComponent={enableCookieComponent} />
    </div>
  );
}

export default SimplifiedSignIn;
